import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { SubscriptionPlan } from '@/lib/database.types';

type PlanInsert = Omit<SubscriptionPlan, 'id' | 'created_at' | 'updated_at'>;

// Stripe sync result types
interface StripeSyncResult {
  success: boolean;
  plan_id: string;
  plan_name: string;
  stripe_product_id: string;
  prices_updated: Array<{ field: string; old_price_id: string; action: string }>;
  prices_created: Array<{ field: string; price_id: string; amount: number; interval: string }>;
  errors: Array<{ field: string; error: string }>;
  database_updated?: boolean;
}

export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return (data as unknown as SubscriptionPlan[]) || [];
    },
  });
}

export function useUpdatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates, syncToStripe = true }: { 
      id: string; 
      updates: Partial<SubscriptionPlan>;
      syncToStripe?: boolean;
    }) => {
      // Check if we're updating pricing fields
      const pricingFields = [
        'base_price', 
        'per_location_price', 
        'per_register_price',
        'annual_base_price',
        'annual_per_location_price',
        'annual_per_register_price'
      ];
      
      const hasPricingUpdates = pricingFields.some(field => field in updates);

      // If we have pricing updates and syncToStripe is enabled, call the Edge Function
      if (hasPricingUpdates && syncToStripe) {
        try {
          // Call the Stripe sync Edge Function
          const { data: syncData, error: syncError } = await supabase.functions.invoke<StripeSyncResult>(
            'update-stripe-price',
            {
              body: {
                plan_id: id,
                ...updates,
              },
            }
          );

          if (syncError) {
            console.error('Stripe sync error:', syncError);
            // Continue with database update even if Stripe sync fails
            // The user can manually reconcile later
          } else if (syncData) {
            console.log('Stripe sync result:', syncData);
            
            // If Stripe sync was successful and updated the database, we're done
            if (syncData.success && syncData.database_updated) {
              // Refetch the updated plan
              const { data: updatedPlan, error: fetchError } = await supabase
                .from('subscription_plans')
                .select('*')
                .eq('id', id as never)
                .single();
              
              if (fetchError) throw fetchError;
              return updatedPlan as unknown as SubscriptionPlan;
            }
            
            // If there were partial errors, log them but continue
            if (syncData.errors && syncData.errors.length > 0) {
              console.warn('Stripe sync had partial errors:', syncData.errors);
            }
          }
        } catch (err) {
          console.error('Failed to sync with Stripe:', err);
          // Continue with database update even if Edge Function fails
        }
      }

      // Update the database directly (fallback or for non-pricing updates)
      const { data, error } = await supabase
        .from('subscription_plans')
        .update(updates as never)
        .eq('id', id as never)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as SubscriptionPlan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}

export function useCreatePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (plan: PlanInsert) => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .insert(plan as never)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as SubscriptionPlan;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}
